import { useMemo, useState } from "react";
import { X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useI18n } from '@/hooks/useI18n';

interface TagInputProps {
  /** Tag già esistenti in libreria (personale/agenzia), proposti come chip selezionabili. */
  availableTags: string[];
  /** Tag correntemente scelti per l'entry in fase di salvataggio. */
  selectedTags: string[];
  onChange: (tags: string[]) => void;
}

/**
 * Input multi-tag riusabile per la libreria attività: mostra i tag esistenti come
 * chip cliccabili (multi-select), un campo di testo libero per crearne di nuovi al
 * volo (Invio o virgola per confermare), e i tag scelti come chip rimovibili.
 * Non esiste nel repo un componente combobox multi-select con creazione al volo
 * riusabile per questo caso (il campo `interests` di profilo/agenzia usa invece
 * un set fisso di opzioni con toggle, non testo libero), quindi viene creato qui,
 * specifico per il modulo libreria.
 */
export function TagInput({ availableTags, selectedTags, onChange }: TagInputProps) {
  const { t } = useI18n();
  const [inputValue, setInputValue] = useState('');

  const suggestions = useMemo(
    () => availableTags.filter((tag) => !selectedTags.includes(tag)),
    [availableTags, selectedTags]
  );

  const addTag = (rawTag: string) => {
    const tag = rawTag.trim();
    if (!tag || selectedTags.includes(tag)) return;
    onChange([...selectedTags, tag]);
  };

  const removeTag = (tag: string) => {
    onChange(selectedTags.filter((existing) => existing !== tag));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(inputValue);
      setInputValue('');
    } else if (e.key === 'Backspace' && !inputValue && selectedTags.length > 0) {
      // Campo vuoto + Backspace: rimuove comodamente l'ultimo tag selezionato.
      removeTag(selectedTags[selectedTags.length - 1]);
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addTag(inputValue);
      setInputValue('');
    }
  };

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label>{t('library.tagInputLabel')}</Label>

        {selectedTags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedTags.map((tag) => (
              <span
                key={tag}
                className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-gray-900 text-white"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="hover:text-gray-300"
                  aria-label={t('library.tagInputRemoveTag')}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={t('library.tagInputPlaceholder')}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-gray-500 font-normal">
          {t('library.tagInputExistingLabel')}
        </Label>
        <div className="flex flex-wrap gap-2">
          {suggestions.length > 0 ? (
            suggestions.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => addTag(tag)}
                className="text-xs px-2.5 py-1 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
              >
                {tag}
              </button>
            ))
          ) : (
            <p className="text-xs text-gray-400 italic">{t('library.tagInputEmptyState')}</p>
          )}
        </div>
      </div>
    </div>
  );
}
